import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type * as React from "react";
import cn from "twsx";
import styles from "./List.module.css";

// Static rows outside FilterList (resource libraries, requirement lists):
// `ListRows` divides its rows on a hairline, `ListRow` is one 44px Linear row
// and `ListRowTarget` is the row's main click target (a button, or a link via
// `asChild`) that fills the remaining width.
export function ListRows({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex min-w-0 flex-col", styles.dividedRows, className)} {...props} />;
}

const listRowVariants = cva("group/list-row flex min-w-0 items-center gap-3 px-4", {
  variants: {
    size: {
      md: "min-h-row-lg py-2",
      lg: "min-h-16 py-3",
    },
    interactive: {
      true: "hover:bg-fill-hover-subtle",
      false: "",
    },
  },
  defaultVariants: { size: "md", interactive: false },
});

/** Use when static rows sit outside FilterList, such as resource or requirement lists. */
export function ListRow({ className, size, interactive, ...props }: React.ComponentProps<"div"> & VariantProps<typeof listRowVariants>) {
  return <div className={cn(listRowVariants({ size, interactive }), className)} {...props} />;
}

const listRowTargetVariants = cva(
  [
    "flex min-w-0 flex-1 gap-3 rounded-md text-left outline-none disabled:cursor-not-allowed disabled:opacity-disabled",
    "focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent",
  ],
  {
    variants: {
      layout: {
        row: "items-center",
        stack: "w-full flex-col items-start",
        option: "h-full items-center justify-between gap-2",
        nav: [
          "min-h-8 items-center justify-between gap-2 px-2 text-base text-text-secondary",
          "hover:bg-fill-hover focus-visible:outline-offset-0",
          "aria-[current=page]:bg-fill-row-selected aria-[current=page]:font-medium aria-[current=page]:text-text-primary",
        ],
      },
    },
    defaultVariants: { layout: "row" },
  },
);

export function ListRowTarget({ asChild, layout, className, type = "button", ...props }: React.ComponentProps<"button"> & VariantProps<typeof listRowTargetVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button";
  return <Comp type={asChild ? undefined : type} className={cn(listRowTargetVariants({ layout }), className)} {...props} />;
}

// A collapsible navigation tree (resource collections): `NavTree` is the
// framed nav surface, `NavTreeItem` one selectable row with a trailing count,
// `NavTreeGroup` an unselectable heading row, `NavTreeRow` lays out the
// expand toggle and `NavTreeChildren` indents a
// nested level on a guide line.
export function NavTree({ className, ...props }: React.ComponentProps<"nav">) {
  return <nav className={cn("min-h-0 overflow-y-auto rounded-settings-list-view-item border border-border-popover bg-bg-surface p-1", className)} {...props} />;
}

export function NavTreeItem({ label, count, selected = false, ...props }: Omit<React.ComponentProps<"button">, "children" | "className"> & { label: React.ReactNode; count?: React.ReactNode; selected?: boolean }) {
  return (
    <ListRowTarget layout="nav" aria-current={selected ? "page" : undefined} {...props}>
      <span className="truncate">{label}</span>
      <span className="shrink-0 text-sm tabular-nums text-text-tertiary">{count}</span>
    </ListRowTarget>
  );
}

export function NavTreeGroup({ label, count, icon }: { label: React.ReactNode; count?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex min-h-8 min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 text-base font-medium text-text-secondary">
      <span className="flex min-w-0 items-center gap-1.5 [&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:text-text-tertiary">
        {icon}
        <span className="truncate">{label}</span>
      </span>
      <span className="shrink-0 text-sm tabular-nums text-text-tertiary">{count}</span>
    </div>
  );
}

// One tree level: the expand toggle (or an equal-width gap) then the row.
export function NavTreeRow({ toggle, children }: { toggle?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-0.5">
      <span className="flex w-7 shrink-0 justify-center">{toggle}</span>
      {children}
    </div>
  );
}

export function NavTreeChildren({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("ml-3.5 border-l pl-2", styles.subtleBorder, className)} {...props} />;
}
